import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Container, Grid, Card, CardContent, Chip, Button,
  Dialog, DialogTitle, DialogContent, IconButton, Divider,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, X, ArrowRight, Terminal, CornerDownRight } from 'lucide-react';

/* ─── Content model ───────────────────────────────────── */
type Block =
  | { type: 'para'; text: string }
  | { type: 'heading'; text: string }
  | { type: 'code'; label?: 'Input' | 'Output' | 'Syntax'; text: string }
  | { type: 'bullets'; items: string[] }
  | { type: 'note'; text: string };

type Question = {
  id: string;
  title: string;
  category: string;
  summary: string;
  blocks: Block[];
};

/* ─── Categories (by Python topic) ────────────────────── */
const CATEGORY_COLORS: Record<string, string> = {
  List: '#10B981',
  Tuple: '#8B5CF6',
  Dictionary: '#F59E0B',
  String: '#06B6D4',
  Sets: '#EC4899',
  Functions: '#EF4444',
};
const catColor = (c: string) => CATEGORY_COLORS[c] ?? '#2563EB';

/* ─── Questions ───────────────────────────────────────── */
const questions: Question[] = [
  {
    id: 'append-extend',
    title: 'Difference Between append() and extend() in Python',
    category: 'List',
    summary: 'Both add data to a list, but append() adds one element while extend() adds each element of an iterable.',
    blocks: [
      { type: 'para', text: 'In Python, both append() and extend() are list methods used to add data to a list. Although they look similar, they work differently.' },
      { type: 'heading', text: 'append()' },
      { type: 'para', text: 'The append() method is used to add any Python object to the end of a list as a single element.' },
      { type: 'para', text: 'If you pass a list to append(), the entire list is added as one element, resulting in a nested list.' },
      { type: 'code', label: 'Input', text: 'list1 = [1, 2, 3]\nlist1.append([4, 5])' },
      { type: 'code', label: 'Output', text: '[1, 2, 3, [4, 5]]' },
      { type: 'para', text: 'In this example, [4, 5] is added as a single element at the end of the list.' },
      { type: 'heading', text: 'extend()' },
      { type: 'para', text: 'The extend() method is used to add all elements of an iterable (such as a list, tuple, set, or string) to the end of a list.' },
      { type: 'para', text: 'Instead of adding the iterable as a single element, it adds each element individually.' },
      { type: 'code', label: 'Input', text: 'list1 = [1, 2, 3]\nlist1.extend([4, 5])' },
      { type: 'code', label: 'Output', text: '[1, 2, 3, 4, 5]' },
      { type: 'para', text: 'In this example, 4 and 5 are added as separate elements to the list.' },
      { type: 'heading', text: 'Key Differences' },
      { type: 'bullets', items: [
        'append() adds a single element to the list.',
        'extend() adds all elements of an iterable to the list.',
        'Passing a list to append() creates a nested list.',
        'Passing a list to extend() adds each element individually.',
        'append() increases the list size by one.',
        'extend() increases the list size by the number of elements in the iterable.',
      ] },
    ],
  },
  {
    id: 'insert',
    title: 'insert() Method in Python',
    category: 'List',
    summary: 'Adds an element at a specific index, shifting existing elements to the right.',
    blocks: [
      { type: 'para', text: 'The insert() method is used to add an element at a specific position (index) in a list.' },
      { type: 'para', text: 'Unlike append(), which always adds an element at the end of the list, insert() allows you to choose the exact position where the new element should be placed.' },
      { type: 'heading', text: 'Syntax' },
      { type: 'code', label: 'Syntax', text: 'list_name.insert(index, element)' },
      { type: 'bullets', items: [
        'index → The position where the new element will be inserted.',
        'element → The value you want to add to the list.',
      ] },
      { type: 'heading', text: 'Example' },
      { type: 'code', label: 'Input', text: 'list1 = [10, 20, 30]\nlist1.insert(1, 15)' },
      { type: 'code', label: 'Output', text: '[10, 15, 20, 30]' },
      { type: 'para', text: 'In this example, 15 is inserted at index 1, and the existing elements are shifted one position to the right.' },
    ],
  },
  {
    id: 'sort-sorted',
    title: 'Difference Between sort() and sorted() in Python',
    category: 'List',
    summary: 'sort() reorders the original list in place; sorted() returns a new sorted list and works on any iterable.',
    blocks: [
      { type: 'para', text: 'Both sort() and sorted() are used to arrange elements in ascending or descending order. However, they work differently.' },
      { type: 'heading', text: 'sort()' },
      { type: 'para', text: 'The sort() method is a list method that sorts the original list in place. This means it modifies the existing list and does not create a new one.' },
      { type: 'code', label: 'Input', text: 'numbers = [5, 2, 8, 1, 4]\nnumbers.sort()' },
      { type: 'code', label: 'Output', text: '[1, 2, 4, 5, 8]' },
      { type: 'para', text: 'In this example, the original list is permanently sorted in ascending order.' },
      { type: 'heading', text: 'sorted()' },
      { type: 'para', text: 'The sorted() function returns a new sorted list without modifying the original iterable.' },
      { type: 'para', text: 'It can be used with lists, tuples, strings, sets, dictionaries, and other iterable objects.' },
      { type: 'code', label: 'Input', text: 'numbers = [5, 2, 8, 1, 4]\nnew_list = sorted(numbers)' },
      { type: 'code', label: 'Output', text: 'Original List:  [5, 2, 8, 1, 4]\nNew Sorted List: [1, 2, 4, 5, 8]' },
      { type: 'para', text: 'In this example, the original list remains unchanged, while sorted() creates a new sorted list.' },
      { type: 'heading', text: 'Sorting in Descending Order' },
      { type: 'para', text: 'Both sort() and sorted() support the reverse=True argument.' },
      { type: 'code', label: 'Input', text: 'numbers = [5, 2, 8, 1, 4]\nnumbers.sort(reverse=True)' },
      { type: 'code', label: 'Output', text: '[8, 5, 4, 2, 1]' },
      { type: 'code', label: 'Input', text: 'numbers = [5, 2, 8, 1, 4]\nnew_list = sorted(numbers, reverse=True)' },
      { type: 'code', label: 'Output', text: '[8, 5, 4, 2, 1]' },
      { type: 'heading', text: 'Key Differences' },
      { type: 'bullets', items: [
        'sort() is a list method, whereas sorted() is a built-in Python function.',
        'sort() modifies the original list.',
        'sorted() returns a new sorted list without changing the original data.',
        'sort() works only with lists.',
        'sorted() works with any iterable, such as lists, tuples, strings, sets, and dictionaries.',
        'sort() returns None.',
        'sorted() returns a new sorted list.',
      ] },
    ],
  },
  {
    id: 'pop-remove-del-clear',
    title: 'Difference Between pop(), remove(), del, and clear() in Python',
    category: 'List',
    summary: 'Four ways to delete from a list — by index, by value, by slice/keyword, or all at once.',
    blocks: [
      { type: 'para', text: 'In Python, pop(), remove(), del, and clear() are used to delete elements from a list. However, each one works differently.' },
      { type: 'heading', text: 'pop()' },
      { type: 'para', text: 'The pop() method is used to remove an element from a list using its index. It also returns the removed element. If no index is provided, pop() removes the last element of the list.' },
      { type: 'code', label: 'Input', text: 'numbers = [10, 20, 30, 40]\nnumbers.pop()' },
      { type: 'code', label: 'Output', text: '[10, 20, 30]' },
      { type: 'para', text: 'In this example, the last element (40) is removed.' },
      { type: 'code', label: 'Input', text: 'numbers = [10, 20, 30, 40]\nnumbers.pop(1)' },
      { type: 'code', label: 'Output', text: '[10, 30, 40]' },
      { type: 'para', text: 'Here, the element at index 1 (20) is removed.' },
      { type: 'heading', text: 'remove()' },
      { type: 'para', text: 'The remove() method is used to remove an element by its value. If the value appears multiple times, only the first occurrence is removed.' },
      { type: 'code', label: 'Input', text: 'numbers = [10, 20, 30, 20, 40]\nnumbers.remove(20)' },
      { type: 'code', label: 'Output', text: '[10, 30, 20, 40]' },
      { type: 'para', text: 'Here, only the first 20 is removed.' },
      { type: 'note', text: 'Note: If the specified value is not found in the list, Python raises a ValueError.' },
      { type: 'heading', text: 'del' },
      { type: 'para', text: 'The del keyword is used to delete an element, a slice of elements, or even the entire list.' },
      { type: 'code', label: 'Input', text: '# Delete an element\nnumbers = [10, 20, 30, 40]\ndel numbers[1]' },
      { type: 'code', label: 'Output', text: '[10, 30, 40]' },
      { type: 'code', label: 'Input', text: '# Delete multiple elements\nnumbers = [10, 20, 30, 40, 50]\ndel numbers[1:4]' },
      { type: 'code', label: 'Output', text: '[10, 50]' },
      { type: 'code', label: 'Input', text: '# Delete the entire list\nnumbers = [10, 20, 30]\ndel numbers' },
      { type: 'para', text: 'After deleting the list, it no longer exists in memory.' },
      { type: 'heading', text: 'clear()' },
      { type: 'para', text: 'The clear() method removes all elements from the list, making it an empty list. The list itself still exists.' },
      { type: 'code', label: 'Input', text: 'numbers = [10, 20, 30, 40]\nnumbers.clear()' },
      { type: 'code', label: 'Output', text: '[]' },
      { type: 'para', text: 'Here, all elements are removed, but the list object remains available.' },
      { type: 'heading', text: 'Key Differences' },
      { type: 'bullets', items: [
        'pop() removes an element by index and returns the removed element.',
        'remove() removes an element by value and deletes only the first matching value.',
        'del can delete a single element, multiple elements, or the entire list.',
        'clear() removes all elements from the list but keeps the list object.',
      ] },
    ],
  },
  {
    id: 'indexing-slicing',
    title: 'Indexing and Slicing in Python Strings',
    category: 'String',
    summary: 'Access single characters with positive/negative indexing, and extract portions using slicing [start:stop:step].',
    blocks: [
      { type: 'para', text: 'In Python, indexing and slicing are used to access characters from a string.' },
      { type: 'heading', text: 'Indexing in String' },
      { type: 'para', text: 'Indexing is used to access a single character from a string using its position, called an index.' },
      { type: 'para', text: 'Python supports two types of indexing:' },
      { type: 'bullets', items: [
        'Positive Indexing → Starts from left to right with 0',
        'Negative Indexing → Starts from right to left with -1',
      ] },
      { type: 'heading', text: 'Example' },
      { type: 'code', label: 'Input', text: 'text = "PYTHON"' },
      { type: 'para', text: 'Positive Indexing:' },
      { type: 'code', text: 'P → 0\nY → 1\nT → 2\nH → 3\nO → 4\nN → 5' },
      { type: 'para', text: 'Negative Indexing:' },
      { type: 'code', text: 'P → -6\nY → -5\nT → -4\nH → -3\nO → -2\nN → -1' },
      { type: 'heading', text: 'Accessing a Character' },
      { type: 'code', label: 'Input', text: 'text = "PYTHON"\ntext[0]' },
      { type: 'code', label: 'Output', text: 'P' },
      { type: 'code', label: 'Input', text: 'text[-1]' },
      { type: 'code', label: 'Output', text: 'N' },
      { type: 'para', text: 'Here, text[0] returns the first character, while text[-1] returns the last character.' },
      { type: 'heading', text: 'Slicing in String' },
      { type: 'para', text: 'Slicing is used to access a part or portion of a string.' },
      { type: 'para', text: 'Instead of accessing only one character, slicing allows us to extract multiple characters.' },
      { type: 'heading', text: 'Syntax' },
      { type: 'code', label: 'Syntax', text: 'string[start : stop : step]' },
      { type: 'bullets', items: [
        'start → Index where slicing begins.',
        'stop → Index where slicing ends, but the stop index is not included.',
        'step → Number of positions to move at a time.',
      ] },
      { type: 'heading', text: 'Example' },
      { type: 'code', label: 'Input', text: 'text = "PYTHON"\ntext[0:3]' },
      { type: 'code', label: 'Output', text: 'PYT' },
      { type: 'para', text: 'The slicing starts from index 0 and stops before index 3. Therefore:' },
      { type: 'bullets', items: ['Index 0 → P', 'Index 1 → Y', 'Index 2 → T'] },
      { type: 'heading', text: 'Omitting the Start Index' },
      { type: 'para', text: 'If the start index is not provided, Python starts from the beginning of the string.' },
      { type: 'code', label: 'Input', text: 'text = "PYTHON"\ntext[:3]' },
      { type: 'code', label: 'Output', text: 'PYT' },
      { type: 'heading', text: 'Omitting the Stop Index' },
      { type: 'para', text: 'If the stop index is not provided, Python continues until the end of the string.' },
      { type: 'code', label: 'Input', text: 'text = "PYTHON"\ntext[3:]' },
      { type: 'code', label: 'Output', text: 'HON' },
      { type: 'heading', text: 'Using Step in Slicing' },
      { type: 'para', text: 'The step value determines how many positions Python moves after selecting each character.' },
      { type: 'code', label: 'Input', text: 'text = "PYTHON"\ntext[::2]' },
      { type: 'code', label: 'Output', text: 'PTO' },
      { type: 'para', text: 'Here, Python selects every second character.' },
      { type: 'heading', text: 'Reversing a String' },
      { type: 'para', text: 'A string can be reversed using a negative step.' },
      { type: 'code', label: 'Input', text: 'text = "PYTHON"\ntext[::-1]' },
      { type: 'code', label: 'Output', text: 'NOHTYP' },
      { type: 'heading', text: 'Key Difference Between Indexing and Slicing' },
      { type: 'bullets', items: [
        'Indexing → Used to access a single character.',
        'Slicing → Used to access multiple characters or a part of the string.',
        'Indexing uses string[index].',
        'Slicing uses string[start:stop:step].',
      ] },
    ],
  },
  {
    id: 'palindrome',
    title: 'Palindrome in Python String',
    category: 'String',
    summary: 'A word or number that reads the same forwards and backwards — check it with text == text[::-1].',
    blocks: [
      { type: 'para', text: 'A palindrome is a word, number, or string that reads the same forward and backward.' },
      { type: 'heading', text: 'Examples of Palindrome' },
      { type: 'bullets', items: ['MADAM', 'LEVEL', 'RADAR', 'MOM', '121'] },
      { type: 'para', text: 'For example: MADAM → Reverse → MADAM' },
      { type: 'para', text: 'Since the original string and the reversed string are the same, MADAM is a palindrome.' },
      { type: 'heading', text: 'Check Palindrome Using String Slicing' },
      { type: 'para', text: 'In Python, we can reverse a string using:' },
      { type: 'code', text: 'text[::-1]' },
      { type: 'para', text: 'Then, we compare the original string with the reversed string.' },
      { type: 'heading', text: 'Example' },
      { type: 'code', label: 'Input', text: 'text = "madam"\n\nif text == text[::-1]:\n    print("Palindrome")\nelse:\n    print("Not Palindrome")' },
      { type: 'code', label: 'Output', text: 'Palindrome' },
    ],
  },
  {
    id: 'split-join',
    title: 'Difference Between split() and join() in Python String',
    category: 'String',
    summary: 'split() breaks a string into a list; join() combines an iterable of strings into a single string.',
    blocks: [
      { type: 'para', text: 'In Python, split() and join() are string methods used to separate and combine strings.' },
      { type: 'heading', text: 'split() Method' },
      { type: 'para', text: 'The split() method is used to divide a string into multiple parts and returns them as a list.' },
      { type: 'para', text: 'By default, split() separates the string based on spaces.' },
      { type: 'heading', text: 'Example' },
      { type: 'code', label: 'Input', text: 'text = "Hello World Python"\nresult = text.split()' },
      { type: 'code', label: 'Output', text: "['Hello', 'World', 'Python']" },
      { type: 'para', text: 'Here, the string is divided into separate words and returned as a list.' },
      { type: 'heading', text: 'split() Using a Separator' },
      { type: 'para', text: 'We can also specify a separator inside split().' },
      { type: 'code', label: 'Input', text: 'text = "Apple,Mango,Banana"\nresult = text.split(",")' },
      { type: 'code', label: 'Output', text: "['Apple', 'Mango', 'Banana']" },
      { type: 'para', text: 'Here, the comma , is used as the separator.' },
      { type: 'heading', text: 'join() Method' },
      { type: 'para', text: 'The join() method is used to combine multiple strings from an iterable into a single string.' },
      { type: 'heading', text: 'Syntax' },
      { type: 'code', label: 'Syntax', text: 'separator.join(iterable)' },
      { type: 'heading', text: 'Example' },
      { type: 'code', label: 'Input', text: "words = ['Hello', 'World', 'Python']\nresult = \" \".join(words)" },
      { type: 'code', label: 'Output', text: 'Hello World Python' },
      { type: 'para', text: 'Here, a space is used to join all the elements.' },
      { type: 'heading', text: 'join() Using a Separator' },
      { type: 'code', label: 'Input', text: "fruits = ['Apple', 'Mango', 'Banana']\nresult = \"-\".join(fruits)" },
      { type: 'code', label: 'Output', text: 'Apple-Mango-Banana' },
      { type: 'para', text: 'Here, the hyphen - is placed between each element.' },
      { type: 'heading', text: 'Key Difference Between split() and join()' },
      { type: 'bullets', items: [
        'split() → Converts a string into a list.',
        'join() → Converts an iterable of strings into a single string.',
        'split() separates the data.',
        'join() combines the data.',
      ] },
    ],
  },
  {
    id: 'find-index',
    title: 'Difference Between find() and index() in Python String',
    category: 'String',
    summary: 'Both return the first index of a substring, but find() returns -1 when missing while index() raises a ValueError.',
    blocks: [
      { type: 'para', text: 'In Python, both find() and index() are string methods used to find the position of a character or substring.' },
      { type: 'para', text: 'The main difference is how they behave when the value is not found.' },
      { type: 'heading', text: 'find() Method' },
      { type: 'para', text: 'The find() method searches for a character or substring and returns the index of its first occurrence.' },
      { type: 'para', text: 'If the value is not found, it returns -1.' },
      { type: 'heading', text: 'Example' },
      { type: 'code', label: 'Input', text: 'text = "Hello Python"\nresult = text.find("Python")' },
      { type: 'code', label: 'Output', text: '6' },
      { type: 'para', text: 'Here, "Python" starts at index 6.' },
      { type: 'heading', text: 'When Value Is Not Found' },
      { type: 'code', label: 'Input', text: 'text = "Hello Python"\nresult = text.find("Java")' },
      { type: 'code', label: 'Output', text: '-1' },
      { type: 'para', text: 'Since "Java" is not present in the string, find() returns -1.' },
      { type: 'heading', text: 'index() Method' },
      { type: 'para', text: 'The index() method also searches for a character or substring and returns the index of its first occurrence.' },
      { type: 'para', text: 'However, if the value is not found, it raises a ValueError.' },
      { type: 'heading', text: 'Example' },
      { type: 'code', label: 'Input', text: 'text = "Hello Python"\nresult = text.index("Python")' },
      { type: 'code', label: 'Output', text: '6' },
      { type: 'heading', text: 'When Value Is Not Found' },
      { type: 'code', label: 'Input', text: 'text = "Hello Python"\nresult = text.index("Java")' },
      { type: 'code', label: 'Output', text: 'ValueError' },
      { type: 'para', text: 'Since "Java" is not present in the string, index() raises a ValueError.' },
      { type: 'note', text: 'Tip: Use find() when a missing value is expected and you want to handle it gracefully; use index() when the value should always be present.' },
      { type: 'heading', text: 'Key Difference Between find() and index()' },
      { type: 'bullets', items: [
        'find() → Returns the index if the value is found.',
        'index() → Returns the index if the value is found.',
        'find() → Returns -1 if the value is not found.',
        'index() → Raises a ValueError if the value is not found.',
      ] },
    ],
  },
  {
    id: 'count-vowels',
    title: 'How to Count Vowels in a Python String',
    category: 'String',
    summary: 'Loop through each character and check if it belongs to "aeiou" to count the vowels in a string.',
    blocks: [
      { type: 'para', text: 'In Python, we can count vowels in a string by checking each character using a for loop.' },
      { type: 'para', text: 'The vowels are: a, e, i, o, u' },
      { type: 'heading', text: 'Example' },
      { type: 'code', label: 'Input', text: 'text = "Hello Python"\ncount = 0\n\nfor char in text:\n    if char.lower() in "aeiou":\n        count += 1\n\nprint(count)' },
      { type: 'code', label: 'Output', text: '3' },
      { type: 'heading', text: 'How It Works' },
      { type: 'para', text: 'The string is: Hello Python' },
      { type: 'para', text: 'The vowels are: e, o, o' },
      { type: 'para', text: 'Therefore, the total number of vowels is 3.' },
      { type: 'heading', text: 'Explanation' },
      { type: 'bullets', items: [
        'count = 0 → Creates a variable to store the number of vowels.',
        'for char in text → Checks each character one by one.',
        'char.lower() → Converts the character to lowercase.',
        'char.lower() in "aeiou" → Checks whether the character is a vowel.',
        'count += 1 → Increases the count when a vowel is found.',
      ] },
      { type: 'heading', text: 'Another Example' },
      { type: 'code', label: 'Input', text: 'text = "Education"\ncount = 0\n\nfor char in text:\n    if char.lower() in "aeiou":\n        count += 1\n\nprint(count)' },
      { type: 'code', label: 'Output', text: '5' },
      { type: 'para', text: 'The vowels are: E, u, a, i, o. So, the total number of vowels is 5.' },
    ],
  },
  {
    id: 'f-string',
    title: 'What is an f-string in Python and Why Do We Use It?',
    category: 'String',
    summary: 'A modern, readable way to embed variables, expressions, methods and formatted numbers directly inside a string.',
    blocks: [
      { type: 'para', text: 'An f-string (formatted string literal) is a simple and modern way to insert variables or expressions directly inside a string.' },
      { type: 'para', text: 'To create an f-string, add the letter f before the opening quotation mark and place variables or expressions inside curly braces {}.' },
      { type: 'heading', text: 'Syntax' },
      { type: 'code', label: 'Syntax', text: 'f"Text {variable}"' },
      { type: 'heading', text: 'Example' },
      { type: 'code', label: 'Input', text: 'name = "Vikash"\nage = 25\n\nprint(f"My name is {name} and I am {age} years old.")' },
      { type: 'code', label: 'Output', text: 'My name is Vikash and I am 25 years old.' },
      { type: 'para', text: 'Here, the values of name and age are automatically inserted into the string.' },
      { type: 'heading', text: 'Why Do We Use f-strings?' },
      { type: 'para', text: 'Without an f-string, we may need to join strings and variables manually.' },
      { type: 'para', text: 'Example without an f-string:' },
      { type: 'code', label: 'Input', text: 'name = "Vikash"\nage = 25\n\nprint("My name is " + name + " and I am " + str(age) + " years old.")' },
      { type: 'para', text: 'With an f-string, the same code becomes simpler:' },
      { type: 'code', label: 'Input', text: 'print(f"My name is {name} and I am {age} years old.")' },
      { type: 'para', text: 'F-strings make the code:' },
      { type: 'bullets', items: ['Easier to read', 'Easier to write', 'Cleaner', 'More convenient for formatting values'] },
      { type: 'heading', text: 'Using Expressions in f-strings' },
      { type: 'para', text: 'We can also perform calculations directly inside {}.' },
      { type: 'code', label: 'Input', text: 'a = 10\nb = 20\n\nprint(f"Sum = {a + b}")' },
      { type: 'code', label: 'Output', text: 'Sum = 30' },
      { type: 'heading', text: 'Using Methods in f-strings' },
      { type: 'para', text: 'We can also use methods inside an f-string.' },
      { type: 'code', label: 'Input', text: 'name = "python"\n\nprint(f"Language: {name.upper()}")' },
      { type: 'code', label: 'Output', text: 'Language: PYTHON' },
      { type: 'heading', text: 'Formatting Decimal Numbers' },
      { type: 'para', text: 'F-strings can also be used to control the number of decimal places.' },
      { type: 'code', label: 'Input', text: 'price = 99.5678\n\nprint(f"Price: {price:.2f}")' },
      { type: 'code', label: 'Output', text: 'Price: 99.57' },
      { type: 'para', text: 'Here, .2f displays the number with 2 digits after the decimal point.' },
      { type: 'heading', text: 'Conclusion' },
      { type: 'para', text: 'An f-string is used to insert variables, values, and expressions directly inside a string in a simple and readable way.' },
      { type: 'note', text: 'Easy trick to remember → f-string = Format a String with Variables.' },
      { type: 'heading', text: 'Basic Format' },
      { type: 'code', label: 'Syntax', text: 'f"Text {variable}"' },
      { type: 'heading', text: 'Example' },
      { type: 'code', label: 'Input', text: 'name = "Vikash"\n\nprint(f"Hello, {name}")' },
      { type: 'code', label: 'Output', text: 'Hello, Vikash' },
    ],
  },
];

/* ─── Code / block renderer ───────────────────────────── */
const labelStyle: Record<string, { color: string; bg: string }> = {
  Input: { color: '#67E8F9', bg: 'rgba(103,232,249,0.12)' },
  Output: { color: '#86EFAC', bg: 'rgba(134,239,172,0.12)' },
  Syntax: { color: '#C4B5FD', bg: 'rgba(196,181,253,0.14)' },
};

const CodeBlock: React.FC<{ label?: string; text: string }> = ({ label, text }) => (
  <Box sx={{ mt: 1.25, mb: 1.5, borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(148,163,184,0.18)' }}>
    {label && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.75, py: 0.75, bgcolor: '#0B1220', borderBottom: '1px solid rgba(148,163,184,0.14)' }}>
        {label === 'Output' ? <CornerDownRight size={13} color={labelStyle[label]?.color} /> : <Terminal size={13} color={labelStyle[label]?.color ?? '#94A3B8'} />}
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: labelStyle[label]?.color ?? '#94A3B8' }}>
          {label}
        </Typography>
      </Box>
    )}
    <Box
      component="pre"
      sx={{
        m: 0, p: 1.75, bgcolor: '#0B1220', color: '#E2E8F0', overflowX: 'auto',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        fontSize: '0.85rem', lineHeight: 1.7,
      }}
    >
      {text}
    </Box>
  </Box>
);

const BlockView: React.FC<{ block: Block; accent: string }> = ({ block, accent }) => {
  switch (block.type) {
    case 'heading':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 3, mb: 1 }}>
          <Box sx={{ width: 4, height: 20, borderRadius: '4px', bgcolor: accent, flexShrink: 0 }} />
          <Typography
            sx={{
              fontWeight: 800, color: '#0F172A', fontSize: '1.05rem',
              fontFamily: '"Poppins", "Inter", sans-serif', letterSpacing: '-0.01em',
            }}
          >
            {block.text}
          </Typography>
        </Box>
      );
    case 'para':
      return <Typography sx={{ color: '#334155', lineHeight: 1.8, mb: 0.75, fontSize: '0.95rem' }}>{block.text}</Typography>;
    case 'code':
      return <CodeBlock label={block.label} text={block.text} />;
    case 'note':
      return (
        <Box sx={{ my: 1.5, p: 1.5, borderRadius: '12px', bgcolor: '#FFFBEB', border: '1px solid #FDE68A' }}>
          <Typography sx={{ color: '#92400E', fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.7 }}>{block.text}</Typography>
        </Box>
      );
    case 'bullets':
      return (
        <Box component="ul" sx={{ pl: 2.5, my: 1 }}>
          {block.items.map((b, i) => (
            <Box component="li" key={i} sx={{ color: '#334155', lineHeight: 1.75, mb: 0.6, fontSize: '0.93rem', '&::marker': { color: accent } }}>
              {b}
            </Box>
          ))}
        </Box>
      );
    default:
      return null;
  }
};

/* ─── Page ────────────────────────────────────────────── */
const InterviewPrepPythonPage: React.FC = () => {
  const [filter, setFilter] = useState<string>('All');
  const [active, setActive] = useState<Question | null>(null);

  const filtered = useMemo(
    () => (filter === 'All' ? questions : questions.filter((q) => q.category === filter)),
    [filter],
  );

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 8, md: 10 }, pb: 8, bgcolor: '#F8FAFC' }}>

      {/* ── Hero Banner ──────────────────────────────────── */}
      <Box
        sx={{
          position: 'relative', color: '#fff', py: { xs: 6, md: 8 }, px: 2, mb: 6, overflow: 'hidden',
          '&::after': { content: '""', position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0f172a 100%)' },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Target size={22} color="#93c5fd" />
              <Typography sx={{ color: '#93c5fd', fontWeight: 600, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                Interview Preparation
              </Typography>
            </Box>
            <Typography component="h1" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, fontWeight: 800, mb: 2, lineHeight: 1.2 }}>
              Python Interview Preparation
            </Typography>
            <Typography sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, color: 'rgba(255,255,255,0.8)', lineHeight: 1.8, maxWidth: 640 }}>
              Click any question to open a detailed, example-rich explanation. Use the category
              filters to focus on exactly the topics you want to revise.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg">

        {/* ── Filter chips ───────────────────────────────── */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
          {['All', ...Array.from(new Set(questions.map((q) => q.category)))].map((name) => {
            const isActive = filter === name;
            const color = name === 'All' ? '#2563EB' : catColor(name);
            return (
              <Button
                key={name}
                onClick={() => setFilter(name)}
                sx={{
                  bgcolor: isActive ? color : '#fff',
                  color: isActive ? '#fff' : '#334155',
                  border: `1px solid ${isActive ? color : '#E5E7EB'}`,
                  fontWeight: 700, fontSize: '0.82rem', borderRadius: '30px',
                  px: 2.25, py: 0.6, textTransform: 'none',
                  boxShadow: isActive ? `0 4px 12px ${color}33` : 'none',
                  '&:hover': { bgcolor: isActive ? color : '#F8FAFC', borderColor: color },
                }}
              >
                {name}
                {name !== 'All' && (
                  <Box component="span" sx={{ ml: 0.75, opacity: 0.85, fontWeight: 800 }}>
                    {questions.filter((q) => q.category === name).length}
                  </Box>
                )}
              </Button>
            );
          })}
        </Box>

        {/* ── Question cards ─────────────────────────────── */}
        <AnimatePresence mode="wait">
          <motion.div key={filter} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
            <Grid container spacing={2.5}>
              {filtered.map((q, i) => {
                const color = catColor(q.category);
                return (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }} key={q.id}>
                    <motion.div
                      style={{ height: '100%' }}
                      initial={{ opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.05 }}
                    >
                      <Card
                        onClick={() => setActive(q)}
                        sx={{
                          height: '100%', display: 'flex', flexDirection: 'column',
                          cursor: 'pointer', borderRadius: '16px',
                          border: '1px solid #E5E7EB', bgcolor: '#fff',
                          boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                          transition: 'transform 0.25s, box-shadow 0.25s, border-color 0.25s',
                          '&:hover': { transform: 'translateY(-4px)', borderColor: color, boxShadow: `0 12px 28px ${color}1f` },
                        }}
                      >
                        <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.75 }}>
                            <Box sx={{
                              width: 34, height: 34, borderRadius: '10px', bgcolor: `${color}16`, color,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.85rem',
                            }}>
                              {String(i + 1).padStart(2, '0')}
                            </Box>
                            <Chip label={q.category} size="small" sx={{ bgcolor: `${color}12`, color, fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.4, borderRadius: '8px', textTransform: 'uppercase' }} />
                          </Box>
                          <Typography
                            sx={{
                              fontWeight: 800, color: '#0F172A', fontSize: '1.02rem', lineHeight: 1.4,
                              mb: 1, letterSpacing: '-0.01em',
                              minHeight: '2.85em',
                              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                            }}
                          >
                            {q.title}
                          </Typography>
                          <Typography
                            sx={{
                              color: '#64748B', fontSize: '0.88rem', lineHeight: 1.6, mb: 2, flexGrow: 1,
                              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                            }}
                          >
                            {q.summary}
                          </Typography>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color, fontWeight: 800, fontSize: '0.85rem', mt: 'auto' }}>
                            View Answer <ArrowRight size={15} />
                          </Box>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          </motion.div>
        </AnimatePresence>
      </Container>

      {/* ── Answer popup ─────────────────────────────────── */}
      <Dialog
        open={Boolean(active)}
        onClose={() => setActive(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: '18px', border: '1px solid #E5E7EB' } }}
      >
        <DialogTitle sx={{ pr: 7, pb: 1.5 }}>
          {active && (
            <>
              <Chip
                label={active.category}
                size="small"
                sx={{ mb: 1, bgcolor: `${catColor(active.category)}14`, color: catColor(active.category), fontWeight: 700, fontSize: '0.72rem', borderRadius: '8px' }}
              />
              <Typography sx={{ fontWeight: 900, color: '#0F172A', fontSize: '1.15rem', lineHeight: 1.35 }}>
                {active.title}
              </Typography>
            </>
          )}
          <IconButton
            aria-label="close"
            onClick={() => setActive(null)}
            sx={{ position: 'absolute', right: 12, top: 12, color: '#334155', border: '1px solid #E2E8F0', '&:hover': { bgcolor: '#F1F5F9' } }}
          >
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 2.5 }}>
          {active?.blocks.map((block, i) => (
            <BlockView key={i} block={block} accent={catColor(active.category)} />
          ))}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default InterviewPrepPythonPage;
